/**
 * Parent Dashboard — PIN-gated simple view.
 *
 * Flow:
 *   1. Prompt for 4-digit PIN (first-set on first escalation).
 *   2. Render per-child streak + total stars + today's AI summary +
 *      "Arabic at Home" tip.
 *
 * Language: the summary respects the family's locale (en or nl).
 */

import { useEffect, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { api } from '../lib/api';
import type { SummaryResponse } from '@huroof/shared';
import { currentUser, familyMembers, isParentElevated } from '../state/user';

export function ParentDash() {
  const { route } = useLocation();
  const [pin, setPin] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [summaries, setSummaries] = useState<Record<string, SummaryResponse>>({});
  const [streaks, setStreaks] = useState<Record<string, number>>({});
  const [totalStars, setTotalStars] = useState<Record<string, number>>({});
  const locale = (currentUser.value?.locale ?? 'en') as 'en' | 'nl';

  const children = (familyMembers.value ?? []).filter((m) => m.role === 'child');
  const effectiveChildren = children.length > 0
    ? children
    : currentUser.value && currentUser.value.role === 'child'
      ? [currentUser.value]
      : [];

  async function submitPin(e: Event) {
    e.preventDefault();
    if (!/^\d{4}$/.test(pin)) {
      setErr(locale === 'nl' ? 'Voer 4 cijfers in' : 'Enter 4 digits');
      return;
    }
    setBusy(true);
    try {
      await api('/api/auth/parent-pin', { method: 'POST', body: { pin } });
      isParentElevated.value = true;
      setErr(null);
    } catch {
      setErr(locale === 'nl' ? 'Onjuiste code' : 'Wrong code');
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (!isParentElevated.value) return;
    for (const child of effectiveChildren) {
      void api<{ ok: true; summary: SummaryResponse }>(
        `/api/ai/summary/${child.id}?locale=${locale}`,
      ).then((r) => {
        setSummaries((prev) => ({ ...prev, [child.id]: r.summary }));
      });
      void api<{ ok: true; current_streak: number }>(
        `/api/streaks/${child.id}`,
      ).then((r) => {
        setStreaks((prev) => ({ ...prev, [child.id]: r.current_streak }));
      });
      void api<{ ok: true; progress: Array<{ stars: number }> }>(
        `/api/progress/${child.id}`,
      ).then((r) => {
        const total = r.progress.reduce((s, p) => s + p.stars, 0);
        setTotalStars((prev) => ({ ...prev, [child.id]: total }));
      });
    }
  }, [isParentElevated.value, effectiveChildren.length]);

  if (!isParentElevated.value) {
    return (
      <form
        onSubmit={submitPin}
        class="flex-1 flex flex-col items-center justify-center px-6 py-10 gap-6 max-w-md mx-auto w-full"
        dir={locale === 'en' ? 'ltr' : 'ltr'}
      >
        <h1 class="text-3xl font-display text-coral text-center">
          {locale === 'nl' ? 'Ouderdashboard' : 'Parent dashboard'}
        </h1>
        <p class="text-sm text-ink/60 text-center">
          {locale === 'nl' ? 'Voer de 4-cijferige oudercode in' : 'Enter your 4-digit parent PIN'}
        </p>
        <input
          type="tel"
          inputMode="numeric"
          pattern="\d{4}"
          maxLength={4}
          value={pin}
          onInput={(e) => setPin((e.target as HTMLInputElement).value)}
          class="w-48 text-center text-4xl tracking-widest font-display px-4 py-3 rounded-2xl bg-white shadow-sm border border-ink/10"
          placeholder="••••"
        />
        {err && <p class="text-sm text-berry">{err}</p>}
        <button
          type="submit"
          disabled={busy}
          class="min-h-[60px] px-8 py-4 rounded-2xl bg-coral text-white text-xl font-display shadow-xl shadow-coral/40 active:scale-95 disabled:opacity-50"
        >
          {locale === 'nl' ? 'Doorgaan' : 'Continue'}
        </button>
        <button
          type="button"
          onClick={() => route('/')}
          class="text-sm text-ink/50 underline"
        >
          ← {locale === 'nl' ? 'Terug' : 'Back'}
        </button>
      </form>
    );
  }

  return (
    <div class="flex-1 px-4 py-6 pb-24 max-w-2xl mx-auto w-full" dir="ltr">
      <header class="flex items-center justify-between mb-6">
        <button
          type="button"
          onClick={() => route('/')}
          class="text-sm text-ink/60 underline"
        >
          ← {locale === 'nl' ? 'Home' : 'Home'}
        </button>
        <h1 class="text-2xl font-display text-coral">
          {locale === 'nl' ? 'Ouderdashboard' : 'Parent dashboard'}
        </h1>
      </header>

      {effectiveChildren.length === 0 && (
        <p class="text-center text-ink/50 mt-10">
          {locale === 'nl' ? 'Geen kinderen toegevoegd' : 'No children added yet'}
        </p>
      )}

      <div class="space-y-6">
        {effectiveChildren.map((child) => {
          const summary = summaries[child.id];
          const streak = streaks[child.id] ?? 0;
          const stars = totalStars[child.id] ?? 0;

          return (
            <section
              key={child.id}
              class="rounded-3xl bg-white shadow-lg p-5 space-y-3"
            >
              <div class="flex items-center gap-4">
                <img
                  src={`/assets/avatars/${child.avatar ?? 1}.svg`}
                  alt={child.name}
                  class="w-16 h-16 rounded-2xl bg-sand"
                />
                <div>
                  <p class="text-xl font-display">{child.name}</p>
                  <div class="flex gap-4 text-sm text-ink/60 mt-1">
                    <span>🔥 {streak} {locale === 'nl' ? 'dagen' : 'days'}</span>
                    <span>⭐ {stars}</span>
                  </div>
                </div>
              </div>

              {summary ? (
                <>
                  <p class="text-sm leading-relaxed">{summary.summary}</p>

                  {summary.strengths.length > 0 && (
                    <div>
                      <p class="text-xs font-bold text-leaf mb-1">
                        {locale === 'nl' ? 'Sterk in' : 'Strengths'}
                      </p>
                      <p class="text-sm text-ink/70">{summary.strengths.join(', ')}</p>
                    </div>
                  )}

                  {summary.needs_practice.length > 0 && (
                    <div>
                      <p class="text-xs font-bold text-berry mb-1">
                        {locale === 'nl' ? 'Kan nog beter' : 'Needs practice'}
                      </p>
                      <p class="text-sm text-ink/70">
                        {summary.needs_practice.join(', ')}
                      </p>
                    </div>
                  )}

                  {summary.home_activity && (
                    <div class="rounded-2xl bg-sand p-3">
                      <p class="text-xs font-bold text-coral mb-1">
                        {locale === 'nl' ? 'Arabisch thuis' : 'Arabic at home'}
                      </p>
                      <p class="text-sm">{summary.home_activity}</p>
                    </div>
                  )}
                </>
              ) : (
                <p class="text-sm text-ink/40 italic">
                  {locale === 'nl' ? 'Samenvatting wordt gemaakt…' : 'Generating summary…'}
                </p>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
